/**
 * "Ошибки и ключевые моменты" card
 *  - Operator errors from speech analysis
 *  - Key moments from keyMoments array (with speaker prefix)
 */

import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { AnalysisSection, Tag, type XMLAnalysisResult } from './analysis-utils';
import { describeKeyMoment } from './key-moment-view';
import { KeyMomentTime } from './KeyMomentTime';

interface KeyMomentsSectionProps {
  result: XMLAnalysisResult;
  /** Record duration — used to hide a hallucinated out-of-range timecode. */
  durationSeconds?: number;
  onPlaySegment?: (startSeconds: number, durationSeconds?: number) => void;
}

export function KeyMomentsSection({
  result,
  durationSeconds,
  onPlaySegment,
}: KeyMomentsSectionProps) {
  const { t } = useTranslation('records');

  const timelineItems: {
    time?: string;
    startSeconds?: number;
    type: 'error' | 'key';
    text: string;
  }[] = [];

  if (result.speech?.operatorErrors?.errors) {
    for (const err of result.speech.operatorErrors.errors) {
      timelineItems.push({ type: 'error', text: err });
    }
  }
  if (result.keyMoments) {
    for (const m of result.keyMoments) {
      const { time, startSeconds, text } = describeKeyMoment(m, t, durationSeconds);
      // Always 'key'. `importance` says how notable the moment is, NOT that
      // anything went wrong — a high-importance moment is usually the best
      // thing in the call ("client agreed to proceed"). Only operatorErrors
      // above are errors; keying off importance here painted wins red and
      // tagged them "Error" on calls the analysis found no fault with.
      timelineItems.push({ time, startSeconds, type: 'key', text });
    }
  }

  if (timelineItems.length === 0) return null;

  return (
    <AnalysisSection
      title={t('records.analysisPanel.errorsKeyMoments', 'Errors & Key Moments')}
      summary={
        <span className="font-mono text-2xs text-fg-subtle">{timelineItems.length}</span>
      }
    >
      <div className="flex flex-col gap-1.5">
        {timelineItems.map((item, idx) => (
          <div
            key={idx}
            className={cn(
              'flex items-start gap-2 rounded-md px-2.5 py-2 text-xs',
              item.type === 'error' ? 'bg-danger-dim' : 'bg-warning-dim'
            )}
          >
            {item.time && (
              <KeyMomentTime
                time={item.time}
                startSeconds={item.startSeconds}
                onPlaySegment={onPlaySegment}
              />
            )}
            <Tag
              variant={item.type === 'error' ? 'danger' : 'warning'}
              className="shrink-0 text-micro py-0"
            >
              {item.type === 'error'
                ? t('records.analysisPanel.errorTag', 'Error')
                : t('records.analysisPanel.keyTag', 'Key')}
            </Tag>
            <span className="flex-1 text-fg-secondary">{item.text}</span>
          </div>
        ))}
      </div>
    </AnalysisSection>
  );
}
